import { BaseFeature } from '../../core/base-feature.js';
import { BacklogTracker } from './backlog-tracker.js';

// Offline event queue: on reconnect, replay events the desktop persisted while
// we were disconnected. Cursor is stored per-origin so a reload resumes it.
const BACKLOG_CURSOR_KEY = 'inq.sacredTimeline.cursorTs';
const BACKLOG_PAGE = 500;
const BACKLOG_MAX_REPLAY_MS = 7 * 24 * 60 * 60 * 1000; // cap a long absence at 7 days
const BACKLOG_MAX_EVENTS = 20000;                       // hard safety ceiling

// Event types that are state-sync chatter — important for backend logic but
// never user-visible. Dropped on receipt in _handleRealtimeEvent so they don't
// pollute sacredEvents, the latest-event badge, or any downstream counts.
const HIDDEN_EVENT_TYPES = new Set([
    'limit.enforcement_update',
    'limit.usage_update',
    'limit.state_change',
    'limit.check_in',
    'limit.tick',
]);
const HIDDEN_TYPE_PREFIXES = ['loom.', 'metric.', 'debug.', 'internal.'];
// Substring patterns that match heartbeat/keepalive/state-sync chatter from
// any namespace.
const HIDDEN_TYPE_SUBSTRINGS = ['heartbeat', 'keepalive', 'state_sync'];

function _isHiddenEventType(type) {
    if (!type) return false;
    if (HIDDEN_EVENT_TYPES.has(type)) return true;
    if (HIDDEN_TYPE_PREFIXES.some(p => type.startsWith(p))) return true;
    return HIDDEN_TYPE_SUBSTRINGS.some(s => type.includes(s));
}

/**
 * SacredTimelineFeature - Sacred Timeline v2 with Temporal Loom integration
 *
 * Replaces ActionTimelineFeature. Keeps all existing capabilities (query, schedule,
 * approve, cancel, modify, execute) while adding loom_command handling for the
 * real-time state machine (emit, absorb, collapse, extend, etc.)
 *
 * Communicates with the Sacred Timeline (via DataChannel to desktop) to:
 * - Query scheduled/pending/future events
 * - Schedule new actions (chat, journal, interrogation, etc.)
 * - Approve/cancel/modify/execute scheduled events
 * - Receive real-time updates for all Sacred Timeline events
 * - Handle loom commands (emit, absorb, collapse, extend, etc.)
 */
export class SacredTimelineFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Scheduled/future events
        this.scheduledEvents = [];

        // Pending approval events
        this.pendingApprovalEvents = [];

        // All Sacred Timeline events (merged feed)
        this.sacredEvents = [];
        this.sacredTotalCount = 0;
        this.sacredHasMore = true;
        this.sacredIsLoading = false;

        // Event listeners
        this.scheduledCallbacks = new Set();
        this.sacredEventCallbacks = new Set();
        this.pendingApprovalCallbacks = new Set();

        // Loom command listeners (for React hooks)
        this.loomCommandCallbacks = new Set();

        // Advisor Action Queue listeners (for the Suggested Actions inbox)
        this.advisorCallbacks = new Set();

        // Snapshot request deduplication — only one in-flight at a time
        this._snapshotInFlight = false;
        this._snapshotQueued = false;

        // Request/response tracking
        this.pendingRequests = new Map();
        this.requestTimeout = 15000;

        // Offline event queue: exactly-once dedupe + persisted cursor.
        this._backlog = new BacklogTracker({
            load: () => this._loadCursor(),
            save: (ts) => this._saveCursor(ts)
        });
        this._backlogInFlight = false;
    }

    // ==================== Offline event queue ====================

    _loadCursor() {
        try {
            const raw = globalThis.localStorage?.getItem(BACKLOG_CURSOR_KEY);
            const n = raw == null ? null : Number(raw);
            return Number.isFinite(n) ? n : null;
        } catch { return null; }
    }

    _saveCursor(ts) {
        try { globalThis.localStorage?.setItem(BACKLOG_CURSOR_KEY, String(ts)); } catch { /* private mode / disabled */ }
    }

    /**
     * Replay events the desktop persisted while this peer was disconnected. Call
     * on a peer-connected transition. Pages the DB "since the cursor" ascending
     * and feeds each event through the SAME receive path as live pushes, so the
     * BacklogTracker dedupes any overlap with live events (exactly-once) and all
     * downstream listeners see the recovered events in order. Safe to call
     * repeatedly — only one replay runs at a time.
     * @returns {Promise<number>} number of new (non-duplicate) events applied
     */
    async requestBacklog() {
        if (this._backlogInFlight) return 0;
        this._backlogInFlight = true;
        let applied = 0;
        try {
            const since = this._backlog.backlogSince(BACKLOG_MAX_REPLAY_MS);
            let offset = 0;
            for (;;) {
                const events = await this.queryEvents({
                    since,
                    orderBy: 'timestamp',
                    orderDir: 'ASC',
                    limit: BACKLOG_PAGE,
                    offset
                });
                if (!Array.isArray(events) || events.length === 0) break;
                for (const ev of events) {
                    if (this._applyBacklogEvent(ev)) applied++;
                }
                offset += events.length;
                if (events.length < BACKLOG_PAGE || offset >= BACKLOG_MAX_EVENTS) break;
            }
            if (applied > 0) console.log(`[SacredTimeline] Offline backlog replayed ${applied} event(s) since ${new Date(since).toISOString()}`);
        } catch (err) {
            console.warn('[SacredTimeline] Backlog replay failed:', err?.message || err);
        } finally {
            this._backlogInFlight = false;
        }
        return applied;
    }

    /** Feed one backlog event through the live path; returns true if not a dup. */
    _applyBacklogEvent(event) {
        if (!this._backlog.admit(event)) return false;
        this._dispatchRealtime(event);
        return true;
    }

    static getMetadata() {
        return {
            id: 'sacred-timeline',
            name: 'Sacred Timeline',
            version: 'v2.0',
            capabilities: [
                'query_sacred_events',
                'get_scheduled',
                'schedule_action',
                'approve_action',
                'cancel_action',
                'modify_action',
                'execute_now',
                'loom_commands',
                'loom_get_historical_sessions'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[SacredTimeline] Initializing v2...');
        console.log('[SacredTimeline] Initialized');
    }

    async handleMessage(message) {
        const { action, payload } = message;

        switch (action) {
            // Loom commands (new in v2)
            case 'loom_command':
                return this._handleLoomCommand(payload);

            // Legacy query/response handlers
            case 'timeline_query_response':
                return this._handleQueryResponse(payload);

            case 'timeline_get_scheduled_response':
                return this._handleScheduledResponse(payload);

            case 'timeline_schedule_response':
                return this._handleActionResponse('schedule', payload);

            case 'timeline_approve_response':
                return this._handleActionResponse('approve', payload);

            case 'timeline_cancel_response':
                return this._handleActionResponse('cancel', payload);

            case 'timeline_modify_response':
                return this._handleActionResponse('modify', payload);

            case 'timeline_execute_now_response':
                return this._handleActionResponse('execute_now', payload);

            case 'timeline_get_event_response':
                return this._handleActionResponse('get_event', payload);

            case 'timeline_get_causal_chain_response':
                return this._handleActionResponse('get_causal_chain', payload);

            case 'loom_get_historical_sessions_response':
                return this._handleActionResponse('loom_get_historical_sessions', payload);

            // Real-time Sacred Timeline event push
            case 'sacred_timeline_event':
            case 'timeline_event': {
                // Also forward to legacy timeline feature (used by Debug page for DB management)
                const timelineFeature = this.core?.getFeature?.('timeline');
                if (timelineFeature) {
                    timelineFeature.handleMessage({ action: 'timeline_event', payload });
                }
                return this._handleRealtimeEvent(payload);
            }

            default:
                // Check pending requests for generic responses
                if (payload && payload.originalMessageId) {
                    const resolver = this.pendingRequests.get(payload.originalMessageId);
                    if (resolver) {
                        this.pendingRequests.delete(payload.originalMessageId);
                        resolver.resolve(payload);
                        return;
                    }
                }
        }
    }

    // =====================================
    // Loom Commands (new in v2)
    // =====================================

    _handleLoomCommand(command) {
        // Clear snapshot in-flight flag when we receive the response
        if (command?.type === 'SNAPSHOT') {
            this._snapshotInFlight = false;
            this._snapshotQueued = false;
        }
        // Notify all loom command subscribers (React hooks)
        for (const cb of this.loomCommandCallbacks) {
            try {
                cb(command);
            } catch (e) {
                console.error('[SacredTimeline] Loom command callback error:', e);
            }
        }
        // Also emit as event for general listeners
        this.emit('loom_command', command);
    }

    /**
     * Subscribe to loom commands for React hooks
     * @param {Function} callback - Called with each loom command
     * @returns {Function} Unsubscribe function
     */
    onLoomCommand(callback) {
        this.loomCommandCallbacks.add(callback);
        return () => this.loomCommandCallbacks.delete(callback);
    }

    /**
     * Request a full state snapshot from the desktop.
     * Deduplicated — only one request in-flight at a time.
     */
    getSnapshot() {
        if (this._snapshotInFlight) {
            this._snapshotQueued = true;
            console.log('[SacredTimeline] Snapshot already in-flight, queued');
            return;
        }
        this._snapshotInFlight = true;
        this._sendSacredCommand('loom_get_snapshot', {});
        // Auto-clear after a generous timeout (snapshot arrives as a push, not response)
        setTimeout(() => {
            this._snapshotInFlight = false;
            if (this._snapshotQueued) {
                this._snapshotQueued = false;
                this.getSnapshot();
            }
        }, 3000);
    }

    // =====================================
    // Querying Sacred Timeline Events
    // =====================================

    /**
     * Query Sacred Timeline events with a promise-based API.
     * Returns events directly without mutating shared state.
     * @param {Object} options - Query options (types, limit, offset, etc.)
     * @returns {Promise<Array>} Array of matching events
     */
    async queryEvents(options = {}) {
        const payload = await this._sendSacredRequest('timeline_query', options);
        return payload.events || [];
    }

    /**
     * Query Sacred Timeline events (all types - activity, chat, journal, AI, etc.)
     */
    querySacredEvents(options = {}) {
        this.sacredIsLoading = true;
        const messageId = this._sendSacredCommand('timeline_query', options);
        return messageId;
    }

    /**
     * Load events for a specific date range.
     *
     * The backend's `database.queryEvents` reads `since`/`until` as ms
     * timestamps. Earlier this method passed `startDate`/`endDate` as ISO
     * strings — those keys are silently ignored, so the time filter was
     * never applied and every call scanned the entire timeline_events
     * table. Bound the query to the day's window to avoid that.
     */
    loadEventsForDate(date) {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        this.sacredEvents = [];
        this.sacredHasMore = true;
        return this.querySacredEvents({
            since: dayStart.getTime(),
            until: dayEnd.getTime(),
            limit: 500,
            offset: 0
        });
    }

    /**
     * Load more Sacred Timeline events (pagination)
     */
    loadMoreSacredEvents(limit = 100) {
        if (!this.sacredHasMore || this.sacredIsLoading) return null;
        return this.querySacredEvents({
            limit,
            offset: this.sacredEvents.length
        });
    }

    _handleQueryResponse(payload) {
        this.sacredIsLoading = false;

        // Resolve pending request promise (used by MediaGallery and other direct callers)
        if (payload.originalMessageId) {
            const resolver = this.pendingRequests.get(payload.originalMessageId);
            if (resolver) {
                this.pendingRequests.delete(payload.originalMessageId);
                if (payload.error) {
                    resolver.reject(new Error(payload.error));
                } else {
                    resolver.resolve(payload);
                }
                // Don't update shared state for direct request callers —
                // they manage their own state from the promise result
                return;
            }
        }

        if (payload.error) {
            console.error('[SacredTimeline] Query error:', payload.error);
            return;
        }

        const { events = [], total = 0 } = payload;

        if (this.sacredEvents.length === 0) {
            this.sacredEvents = events;
        } else {
            this.sacredEvents = [...this.sacredEvents, ...events];
        }

        this.sacredTotalCount = total;
        this.sacredHasMore = this.sacredEvents.length < total;

        console.log(`[SacredTimeline] Loaded ${events.length} sacred events (total: ${this.sacredEvents.length}/${total})`);
        this._notifySacredEventListeners();
    }

    // =====================================
    // Scheduled Events
    // =====================================

    /**
     * Get all scheduled future events
     */
    getScheduledEvents(options = {}) {
        return this._sendSacredCommand('timeline_get_scheduled', options);
    }

    /**
     * Refresh scheduled events list
     */
    refreshScheduled() {
        return this.getScheduledEvents({});
    }

    _handleScheduledResponse(payload) {
        // Resolve any promise-based caller (fetchScheduled) waiting on this reply.
        // The scheduled response is routed here, not to _handleActionResponse, so
        // without this the _sendSacredRequest promise would hang forever.
        if (payload.originalMessageId && this.pendingRequests.has(payload.originalMessageId)) {
            const resolver = this.pendingRequests.get(payload.originalMessageId);
            this.pendingRequests.delete(payload.originalMessageId);
            if (payload.error) resolver.reject(new Error(payload.error));
            else resolver.resolve(payload);
        }

        if (payload.error) {
            console.error('[SacredTimeline] Get scheduled error:', payload.error);
            return;
        }

        const events = payload.events || [];
        this.scheduledEvents = events;

        // Separate pending approval events
        this.pendingApprovalEvents = events.filter(e => e.status === 'pending_approval');

        console.log(`[SacredTimeline] ${events.length} scheduled events (${this.pendingApprovalEvents.length} pending approval)`);
        this._notifyScheduledListeners();
        this._notifyPendingApprovalListeners();
    }

    // =====================================
    // Action Commands
    // =====================================

    /**
     * Schedule a new action on the Sacred Timeline
     * @param {string} type - Event type (e.g., 'chat.session_started', 'journal.prompt_assigned')
     * @param {Object} eventData - Event data including scheduled_at, title, data, source, etc.
     * @returns {Promise<Object>} The created event
     */
    async scheduleAction(type, eventData) {
        return this._sendSacredRequest('timeline_schedule', { type, eventData });
    }

    /**
     * Approve a pending action
     * @param {number} eventId - Event ID to approve
     * @returns {Promise<Object>} The approved event
     */
    async approveAction(eventId) {
        return this._sendSacredRequest('timeline_approve', { eventId });
    }

    /**
     * Cancel a scheduled/pending action
     * @param {number} eventId - Event ID to cancel
     * @returns {Promise<Object>} The cancelled event
     */
    async cancelAction(eventId) {
        return this._sendSacredRequest('timeline_cancel', { eventId });
    }

    /**
     * Modify a scheduled action
     * @param {number} eventId - Event ID to modify
     * @param {Object} updates - Fields to update
     * @returns {Promise<Object>} The modified event
     */
    async modifyAction(eventId, updates) {
        return this._sendSacredRequest('timeline_modify', { eventId, updates });
    }

    /**
     * Execute a scheduled action immediately
     * @param {number} eventId - Event ID to execute now
     * @returns {Promise<Object>} The executing event
     */
    async executeNow(eventId) {
        return this._sendSacredRequest('timeline_execute_now', { eventId });
    }

    /**
     * Get full details of a Sacred Timeline event
     * @param {number} eventId - Event ID
     * @returns {Promise<Object>} The event with full details
     */
    async getEvent(eventId) {
        return this._sendSacredRequest('timeline_get_event', { eventId });
    }

    /**
     * Get the causal chain for an event
     * @param {number} eventId - Event ID
     * @returns {Promise<Object>} Chain of related events
     */
    async getCausalChain(eventId) {
        return this._sendSacredRequest('timeline_get_causal_chain', { eventId });
    }

    /**
     * Delete a single event and its details permanently
     * @param {string} eventId - Event ID to delete
     * @returns {Promise<Object>} { success, deleted }
     */
    async deleteEvent(eventId) {
        return this._sendSacredRequest('timeline_delete_event', { eventId });
    }

    /**
     * Ask the desktop to classify whether a keyword match is genuinely NSFW
     * or innocuous, given the surrounding snippet context.
     * @returns {Promise<{ isNsfw, confidence, severity, category, reason }|{ error }>}
     */
    async analyzeKeywordContext({ keyword, groupName, url, title, snippets }) {
        return this._sendSacredRequest('analyze_keyword_context', {
            keyword, groupName, url, title, snippets
        });
    }

    _handleActionResponse(actionType, payload) {
        if (payload.error) {
            console.error(`[SacredTimeline] ${actionType} error:`, payload.error);
        }

        // Resolve any pending promise
        if (payload.originalMessageId) {
            const resolver = this.pendingRequests.get(payload.originalMessageId);
            if (resolver) {
                this.pendingRequests.delete(payload.originalMessageId);
                if (payload.error) {
                    resolver.reject(new Error(payload.error));
                } else {
                    resolver.resolve(payload);
                }
            }
        }

        // After any action, refresh the scheduled list
        this.refreshScheduled();
    }

    // =====================================
    // Real-time Events
    // =====================================

    _handleRealtimeEvent(payload) {
        const event = payload.event || payload;
        // Exactly-once: drop a re-delivery (a live push that overlaps a backlog
        // replay, or a doubled push). A genuine status transition is NOT a dup.
        if (!this._backlog.admit(event)) return;
        this._dispatchRealtime(event);
    }

    _dispatchRealtime(event) {
        // Advisor Action Queue events feed the Suggested Actions inbox, not the
        // timeline view. Handle them first so they're never swallowed by the
        // hidden-type filter below. event.data is the proposal row.
        if (event && typeof event.type === 'string' && (event.type.startsWith('advisor.') || event.type.startsWith('policy.'))) {
            this._notifyAdvisorListeners(event.type, event.data || {});
            return;
        }

        // Drop state-sync chatter (limit usage/enforcement ticks, loom
        // internals). They matter for backend logic on the desktop but should
        // never surface in the user-visible timeline.
        if (event && event.type && _isHiddenEventType(event.type)) {
            return;
        }

        // Add to scheduled list if it's a future event
        if (event.status === 'scheduled' || event.status === 'pending_approval') {
            // Update or add to scheduled events
            const idx = this.scheduledEvents.findIndex(e => e.id === event.id);
            if (idx >= 0) {
                this.scheduledEvents = [
                    ...this.scheduledEvents.slice(0, idx),
                    event,
                    ...this.scheduledEvents.slice(idx + 1)
                ];
            } else {
                this.scheduledEvents = [...this.scheduledEvents, event];
            }

            if (event.status === 'pending_approval') {
                this.pendingApprovalEvents = this.scheduledEvents.filter(
                    e => e.status === 'pending_approval'
                );
                this._notifyPendingApprovalListeners();
            }

            this._notifyScheduledListeners();
        }

        // If event was cancelled/completed/executing, update in-place so the
        // reducer's SCHEDULED_EVENT action can merge status changes. This keeps
        // completed scheduled events visible in the timeline.
        if (event.status === 'cancelled' || event.status === 'completed' || event.status === 'executing') {
            const idx = this.scheduledEvents.findIndex(e => e.id === event.id);
            if (idx >= 0) {
                this.scheduledEvents = [
                    ...this.scheduledEvents.slice(0, idx),
                    { ...this.scheduledEvents[idx], ...event },
                    ...this.scheduledEvents.slice(idx + 1)
                ];
            } else {
                // Event wasn't in scheduled list yet — add it
                this.scheduledEvents = [...this.scheduledEvents, event];
            }
            this.pendingApprovalEvents = this.pendingApprovalEvents.filter(e => e.id !== event.id);
            this._notifyScheduledListeners();
            this._notifyPendingApprovalListeners();
        }

        // Add completed events to the sacred events list (if viewing today)
        if (event.status === 'completed' || event.status === 'executing') {
            this.sacredEvents = [event, ...this.sacredEvents];
            this.sacredTotalCount++;
            this._notifySacredEventListeners();
        }

        // Emit to generic listeners
        this.emit('sacred_event', event);
    }

    // =====================================
    // Event Listeners
    // =====================================

    onScheduledChange(callback) {
        this.scheduledCallbacks.add(callback);
        return () => this.scheduledCallbacks.delete(callback);
    }

    // =====================================
    // Advisor Action Queue
    // =====================================

    /**
     * Subscribe to advisor events. callback(eventType, proposal) where
     * eventType is 'advisor.proposed' | 'advisor.executed' | 'advisor.dismissed'.
     */
    onAdvisorEvent(callback) {
        this.advisorCallbacks.add(callback);
        return () => this.advisorCallbacks.delete(callback);
    }

    _notifyAdvisorListeners(eventType, proposal) {
        for (const cb of this.advisorCallbacks) {
            try { cb(eventType, proposal); } catch (e) { console.error('[SacredTimeline] Advisor callback error:', e); }
        }
    }

    /**
     * Fetch the current pending proposals (source of truth for the inbox).
     * Returns { proposals, droppedByCap } so the inbox can show how many
     * lower-priority suggestions the desktop evicted to stay under the cap.
     */
    async listAdvisorPending(limit = 100) {
        const res = await this._sendSacredRequest('advisor_list_pending', { limit });
        return { proposals: res?.proposals || [], droppedByCap: res?.droppedByCap || 0 };
    }

    /** Approve a proposal (optionally with edited params) → executes it on the desktop. */
    approveAdvisor(id, editedParams = null) {
        return this._sendSacredRequest('advisor_approve', { id, editedParams });
    }

    /** Dismiss a proposal without executing; optional feedback {reason, note} trains the policy. */
    dismissAdvisor(id, feedback = null) {
        return this._sendSacredRequest('advisor_dismiss', { id, feedback });
    }

    /** Recent decided/executed autonomous actions for the activity log (with reversibility). */
    async getActivityLog(limit = 50) {
        const res = await this._sendSacredRequest('autonomy_activity_log', { limit });
        return res?.actions || [];
    }

    /** Undo an executed action via its registered reverter. */
    undoAction(id) {
        return this._sendSacredRequest('autonomy_undo', { id });
    }

    /** Dry-run: what would the policy brain do right now (no execution). */
    simulatePolicy() {
        return this._sendSacredRequest('autonomy_policy_simulate', {});
    }

    /**
     * On-demand: decide the next move NOW and put it in the approval inbox
     * (queued in Suggest mode, executed in Act mode). `shuffle` re-rolls for a
     * different move than what's already pending.
     */
    proposeNextMove({ shuffle = false } = {}) {
        return this._sendSacredRequest('autonomy_policy_propose', { shuffle });
    }

    /** Current AI posture on the spectrum, from the desktop (source of truth). */
    async getGlobalAiMode() {
        const res = await this._sendSacredRequest('get_global_ai_mode', {});
        return res?.success ? res.mode : null;
    }

    /** Live presence (policy heartbeat) + arbiter suppression info. */
    async getAutonomyStatus() {
        const res = await this._sendSacredRequest('autonomy_status', {});
        return { presence: res?.presence || null, arbiter: res?.arbiter || null };
    }

    /** Analytics rollups + trust metrics + per-goal scorecards. */
    async getAutonomyAnalytics(sinceMs) {
        const res = await this._sendSacredRequest('autonomy_analytics', { sinceMs });
        return res?.analytics || null;
    }

    /** Compile a free-text instruction into structured guiding goals (no apply). */
    compileGoal(text) {
        return this._sendSacredRequest('autonomy_compile_goal', { text });
    }

    /**
     * Set the AI's durable guidance — the single "drive toward this" objective.
     * Lands on the desktop agenda's persistent objective (sticks across persona
     * switches and turns the proactive loop on). Returns { success, objective }.
     */
    setObjective(text) {
        return this._sendSacredRequest('autonomy_set_objective', { text });
    }

    /** Clear the guidance override so the active persona's default returns. */
    resetObjective() {
        return this._sendSacredRequest('autonomy_reset_objective', {});
    }

    /** Await the current scheduled/future events (for the autonomy fate timeline). */
    async fetchScheduled() {
        const res = await this._sendSacredRequest('timeline_get_scheduled', {});
        return res?.events || [];
    }

    /** Fetch the autonomy director's goal/setpoints + intervention effectiveness. */
    async getAutonomyGoal() {
        const res = await this._sendSacredRequest('autonomy_get_goal', {});
        return { goal: res?.goal || null, effectiveness: res?.effectiveness || [] };
    }

    /** Update the autonomy director's objective / setpoints. */
    setAutonomyGoal(goal) {
        return this._sendSacredRequest('autonomy_set_goal', { goal });
    }

    /** Fetch the per-behavior autonomy config (engine toggles + thresholds). */
    async getAutonomyConfig() {
        const res = await this._sendSacredRequest('autonomy_get_config', {});
        return res?.config || null;
    }

    /** Deep-merge a partial autonomy config (toggles/thresholds) and persist it. */
    setAutonomyConfig(config) {
        return this._sendSacredRequest('autonomy_set_config', { config });
    }

    onSacredEventsChange(callback) {
        this.sacredEventCallbacks.add(callback);
        return () => this.sacredEventCallbacks.delete(callback);
    }

    onPendingApprovalChange(callback) {
        this.pendingApprovalCallbacks.add(callback);
        return () => this.pendingApprovalCallbacks.delete(callback);
    }

    _notifyScheduledListeners() {
        const data = { events: this.scheduledEvents };
        for (const cb of this.scheduledCallbacks) {
            try { cb(data); } catch (e) { console.error('[SacredTimeline] Scheduled callback error:', e); }
        }
    }

    _notifySacredEventListeners() {
        const data = {
            events: this.sacredEvents,
            totalCount: this.sacredTotalCount,
            hasMore: this.sacredHasMore,
            isLoading: this.sacredIsLoading
        };
        for (const cb of this.sacredEventCallbacks) {
            try { cb(data); } catch (e) { console.error('[SacredTimeline] Sacred event callback error:', e); }
        }
    }

    _notifyPendingApprovalListeners() {
        const data = { events: this.pendingApprovalEvents };
        for (const cb of this.pendingApprovalCallbacks) {
            try { cb(data); } catch (e) { console.error('[SacredTimeline] Pending approval callback error:', e); }
        }
    }

    // =====================================
    // Communication helpers
    // =====================================

    /**
     * Send a command to the Sacred Timeline feature on the desktop
     * Uses sendMessage from BaseFeature which stamps feature ID from metadata
     */
    _sendSacredCommand(action, payload) {
        return this.sendMessage(action, payload);
    }

    /**
     * Send a command and return a promise for the response
     */
    _sendSacredRequest(action, payload) {
        return new Promise((resolve, reject) => {
            const messageId = this._sendSacredCommand(action, payload);

            // Store resolver
            this.pendingRequests.set(messageId, { resolve, reject });

            // Timeout
            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    reject(new Error(`Timeout waiting for ${action} response`));
                }
            }, this.requestTimeout);
        });
    }

    // =====================================
    // Unified API (single data source)
    // =====================================

    /**
     * Query all events for display (convenience wrapper around querySacredEvents)
     * Returns past completed + executing events merged chronologically
     */
    queryAllEvents(options = {}) {
        return this.querySacredEvents(options);
    }

    /**
     * Load more past events (backward pagination / infinite scroll)
     * @param {string} before - ISO timestamp to load events before
     * @param {number} limit - Number of events to load
     */
    loadMoreEvents(before, limit = 100) {
        if (!this.sacredHasMore || this.sacredIsLoading) return null;
        return this.querySacredEvents({
            endDate: before,
            limit,
            offset: this.sacredEvents.length
        });
    }

    /**
     * Load historical sessions with cursor-based pagination.
     * @param {Object} params
     * @param {number} [params.before] - Load sessions ending before this timestamp (cursor)
     * @param {number} [params.limit=15] - Max sessions to return
     * @param {number} [params.since] - Legacy: start timestamp
     * @param {number} [params.until] - Legacy: end timestamp
     * @returns {Promise<{ sessions: Array, total: number, eventCount: number }>}
     */
    async loadHistoricalSessions(params = {}) {
        return this._sendSacredRequest('loom_get_historical_sessions', params);
    }

    /**
     * Subscribe to all event changes (activity + scheduled + pending)
     * Fires when any event source updates
     */
    onAllEventsChange(callback) {
        // Subscribe to all three event sources
        const unsub1 = this.onSacredEventsChange(callback);
        const unsub2 = this.onScheduledChange(() => callback({
            events: this.sacredEvents,
            totalCount: this.sacredTotalCount,
            hasMore: this.sacredHasMore,
            isLoading: this.sacredIsLoading
        }));
        return () => { unsub1(); unsub2(); };
    }

    // =====================================
    // Getters
    // =====================================

    getScheduled() { return this.scheduledEvents; }
    getPendingApproval() { return this.pendingApprovalEvents; }
    getSacredEvents() { return this.sacredEvents; }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[SacredTimeline] Cleaning up...');
        this.scheduledEvents = [];
        this.pendingApprovalEvents = [];
        this.sacredEvents = [];
        this.scheduledCallbacks.clear();
        this.sacredEventCallbacks.clear();
        this.pendingApprovalCallbacks.clear();
        this.loomCommandCallbacks.clear();
        this.pendingRequests.clear();
    }
}
